import React, { useState } from "react";
import { Box, Button, Typography, Modal, TextField, Grid } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { createSeries } from "../../graphql/mutations";
import { generateClient } from "aws-amplify/api";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const validationSchema = yup.object({
  title: yup
    .string("Enter the name of the series")
    .required("Name is required"),
  plot: yup.string("Enter the plot of the series").required("Plot is required"),
  description: yup
    .string("Enter the description of the series")
    .required("Description is required"),
  releaseDate: yup
    .string("Enter the release date")
    .required("Release date is required"),
});
const client = generateClient();
const CreateSeries = () => {
  const [open, setOpen] = React.useState(false);
  const [isLoading, setLoading] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const formik = useFormik({
    initialValues: {
      title: "",
      plot: "",
      description: "",
      releaseDate: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Close the modal after submission
      try {
        setLoading(true);
        const response = await client.graphql({
          query: createSeries,
          variables: {
            input: {
              title: values.title,
              plot: values.plot,
              description: values.description,
              releaseDate: new Date(values.releaseDate).toISOString(),
            },
          },
        });
        if (response.data.createSeries) {
          toast.success("Series has been created!");
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    },
  });

  const convertToLocal = (date) => {
    const df = new Intl.DateTimeFormat("default", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      calendar: "iso8601",
      numberingSystem: "latn",
      hourCycle: "h23",
    });
    const parts = df.formatToParts(date).reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  };

  return (
    <>
      <Button variant="contained" onClick={handleOpen}>
        <AddIcon />
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="create-series-modal"
        aria-describedby="create-series-description"
      >
        <form onSubmit={formik.handleSubmit}>
          <Box sx={style}>
            <Typography id="create-series-modal" variant="h6" component="h2">
              Create Series
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Name of the series"
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Plot of the series"
                  name="plot"
                  value={formik.values.plot}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.plot && Boolean(formik.errors.plot)}
                  helperText={formik.touched.plot && formik.errors.plot}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description of the series"
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
              <Grid item xs={12}>
                <label htmlFor="releaseDate">Release Date: </label>
                <input
                  id={"releaseDate"}
                  name="releaseDate"
                  onChange={formik.handleChange}
                  type="date"
                />
              </Grid>
            </Grid>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isLoading}
              type="submit"
              sx={{ mt: 2 }}
            >
              Create Series
            </LoadingButton>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default CreateSeries;
